// Player data derived from the current interaction window.

import { useMemo } from 'react';
import { useRoomInteractionWindow } from './useRoomInteractionWindow';
import { useRoomMyPlayer } from './useRoomMyPlayer';
import { useRoomPlayers } from './useRoomPlayers';
import type { GameElement, RoomPlayer } from '@airhead-games/shared/types';

interface UseRoomPlayersInWindowReturn {
  playersAssigned: RoomPlayer[];
  playersWaitedOn: RoomPlayer[];
  myPlayerAssigned: boolean;
  myPlayerWaitedOn: boolean;
  myPlayerWindowElements: GameElement[];
}

export function useRoomPlayersInWindow(): UseRoomPlayersInWindowReturn {
  const myPlayer = useRoomMyPlayer();
  const { players } = useRoomPlayers();
  const { windowElements, windowWaitingFor } = useRoomInteractionWindow();

  const playerById = useMemo(() => {
    return new Map(players.map((player) => [player.playerId, player]));
  }, [players]);

  const myPlayerAssigned = useMemo(() => {
    if (!myPlayer) {
      return false;
    }

    return windowElements.some((element) => {
      return element.assignedPlayerId === myPlayer.playerId;
    });
  }, [myPlayer, windowElements]);

  const myPlayerWaitedOn = useMemo(() => {
    if (!myPlayer) return false;
    return windowWaitingFor.includes(myPlayer.playerId);
  }, [myPlayer, windowWaitingFor]);

  const myPlayerWindowElements = useMemo(() => {
    if (!myPlayer) return [];
    return windowElements.filter(
      (element) => element.assignedPlayerId === myPlayer.playerId
    );
  }, [myPlayer, windowElements]);

  const playersAssigned = useMemo(() => {
    const assignedPlayerIds = windowElements
      .map((element) => element.assignedPlayerId)
      .filter((playerId): playerId is string => Boolean(playerId));

    return assignedPlayerIds
      .map((playerId) => playerById.get(playerId))
      .filter((player): player is RoomPlayer => Boolean(player));
  }, [windowElements, playerById]);

  const playersWaitedOn = useMemo(() => {
    return windowWaitingFor
      .map((playerId) => playerById.get(playerId))
      .filter((player): player is RoomPlayer => Boolean(player));
  }, [windowWaitingFor, playerById]);

  return {
    playersAssigned,
    playersWaitedOn,
    myPlayerAssigned,
    myPlayerWaitedOn,
    myPlayerWindowElements,
  };
}
